import {
    getStringValue,
    isRecord,
} from '@apps/web/components/phoebe_chat/record_utils';
import { type DataSourceType } from '@libraries/typescript/api_client';

/**
 * Plain-language copy for the tool-approval card (PHO-11300).
 *
 * Coordinators approve actions, not tool calls, so each approval-gated
 * tool gets a question-style title plus a one-sentence summary of what
 * Phoebe is about to do. Copy may only state what the tool arguments
 * prove (counts, field names, message text) — never internal IDs, raw
 * argument schemas, or tool names.
 *
 * `approval_tool_names.json` is the shared UI manifest for backend approval
 * sets, including v3 tools with their own bundle-local approval predicate.
 * Worker and frontend tests assert that every manifest name has curated
 * copy. The generic fallback only covers the deploy-skew window before
 * copy lands for a newly gated tool.
 */

export type ApprovalCopy = {
    readonly title: string;
    readonly summary: string;
    readonly messagePreview: string | null;
    readonly detailList?: {
        readonly label: string;
        readonly itemNoun: string;
        readonly items: ReadonlyArray<string>;
    };
};

export type ApprovalCopyContext = {
    readonly organizationName?: string | null;
    readonly shiftSourceSystem?: DataSourceType | null;
    readonly caregiverName?: string | null;
    readonly clientName?: string | null;
    // Org default for texting non-responders on fill; null while unknown.
    readonly notifyNonRespondersDefault?: boolean | null;
};

type ApprovalArgs = Record<string, unknown> | Array<unknown> | null;

function organizationNameFromContext(
    context?: ApprovalCopyContext,
): string | null {
    const organizationName = context?.organizationName?.trim();
    return organizationName ? organizationName : null;
}

// Anywhere in the string, not just at the start: "Alice — <uuid>" is still an
// internal id in front of a coordinator. Whitespace is collapsed because these
// names render inline and inside multi-line previews, where an embedded
// newline reshapes the card.
const EMBEDDED_UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}/i;

function safeDisplayName(value: string | null): string | null {
    const collapsed = value?.replace(/\s+/g, ' ').trim();
    if (!collapsed || EMBEDDED_UUID_PATTERN.test(collapsed)) {
        return null;
    }
    return collapsed;
}

// Only the EHRs whose shift-note writeback copy exists. Everything else —
// including hhaexchange and a connection type that is not an EHR at all —
// stays unnamed rather than promising a destination Phoebe has no copy for.
function ehrDisplayName(ehr: DataSourceType | null | undefined): string | null {
    switch (ehr) {
        case 'wellsky':
            return 'WellSky';
        case 'axiscare':
            return 'AxisCare';
        default:
            return null;
    }
}

function displayNameFromArgs(
    args: Record<string, unknown>,
    keys: ReadonlyArray<string>,
): string | null {
    for (const key of keys) {
        const value = safeDisplayName(getStringValue(args, key));
        if (value) {
            return value;
        }
    }
    return null;
}

function caregiverMemoryTarget(
    args: Record<string, unknown>,
    context?: ApprovalCopyContext,
): string {
    return (
        safeDisplayName(context?.caregiverName ?? null) ??
        displayNameFromArgs(args, [
            'caregiver_name',
            'caregiverName',
            'display_name',
            'displayName',
            'name',
        ]) ??
        'the caregiver'
    );
}

function clientMemoryTarget(
    args: Record<string, unknown>,
    context?: ApprovalCopyContext,
): string {
    return (
        safeDisplayName(context?.clientName ?? null) ??
        displayNameFromArgs(args, [
            'client_name',
            'clientName',
            'patient_name',
            'patientName',
            'display_name',
            'displayName',
            'name',
        ]) ??
        'the client'
    );
}

function subjectForOrganization(
    subject: string,
    context?: ApprovalCopyContext,
): string {
    const organizationName = organizationNameFromContext(context);
    return organizationName ? `${subject} for ${organizationName}` : subject;
}

function approvalQuestionForOrganization(
    action: string,
    context?: ApprovalCopyContext,
): string {
    const organizationName = organizationNameFromContext(context);
    return organizationName
        ? `${action} for ${organizationName}?`
        : `${action}?`;
}

function countOf(value: unknown): number | null {
    if (Array.isArray(value)) {
        return value.length;
    }
    if (isRecord(value)) {
        return Object.keys(value).length;
    }
    return null;
}

function counted(count: number | null, noun: string, fallback: string): string {
    // An explicit 0 renders as "0 <noun>s" — a coordinator seeing an
    // unexpectedly empty payload should get that signal, not neutral
    // phrasing implying records exist. Only a missing/unparsable arg
    // falls back.
    if (count === null) {
        return fallback;
    }
    return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function stringList(value: unknown): ReadonlyArray<string> | null {
    if (!Array.isArray(value)) {
        return null;
    }
    const items = value.filter(
        (item): item is string => typeof item === 'string' && item.length > 0,
    );
    if (items.length === 0) {
        return null;
    }
    return items;
}

// Argument keys are backend field names. Swapping underscores for spaces is
// not a translation — it puts "max contact attempts 48h" and "auto send
// unclear shift confirmation replies" in front of a scheduler. Curated copy
// per key, with the mechanical fallback kept for keys added later.
//
// Lower case: these render inline inside a sentence, as
// "Phoebe will change these recommendation defaults: <label> 20 → 25".
const SETTING_FIELD_LABELS: Record<string, string> = {
    // Recommendation defaults
    commute_time_min: 'commute buffer (minutes)',
    ignored_patient_tags: 'client tags to ignore',
    match_patient_office: 'limit to the client’s office',
    match_patient_tags: 'match client tags',
    max_consecutive_days: 'max days in a row',
    max_contact_attempts_48h: 'max contacts per 48 hours',
    max_daily_hours: 'daily hours cap',
    max_distance_mi: 'max travel distance (miles)',
    max_weekly_hours: 'weekly hours cap',
    overtime_behavior: 'caregivers over the weekly cap',
    // Organization general settings
    auto_send_all_replies: 'auto-reply to everything',
    auto_send_callout_decline_response:
        'auto-reply when a caregiver declines a callout',
    auto_send_negative_shift_confirmation_replies:
        'auto-reply to shift declines',
    auto_send_positive_shift_confirmation_replies:
        'auto-reply to shift confirmations',
    auto_send_unclear_shift_confirmation_replies:
        'auto-reply to unclear responses',
    axiscare_service_rate_selection_enabled: 'AxisCare service rate selection',
    call_recording_notice_enabled: 'play the call-recording notice',
    clock_in_grace_minutes: 'clock-in grace period (minutes)',
    clock_out_grace_minutes: 'clock-out grace period (minutes)',
    clock_reminder_monitor_window_hours:
        'missed clock in/out EHR check window (hours)',
    default_notify_non_responders_on_fill:
        'notify caregivers who haven’t responded when filling (default)',
    display_caregiver_names_last_first: 'show caregiver names as last, first',
    duty_completion_grace_minutes:
        'care task completion grace period (minutes)',
    enable_clock_reminders_in_test_mode: 'send clock reminders in test mode',
    engaged_caregiver_threshold: 'engaged caregiver threshold (%)',
    inbound_voice_agent_enabled: 'answer inbound calls',
    inbound_voice_shift_cancellation_enabled:
        'let callers cancel shifts by phone',
    inbound_voice_shift_cancellation_transfer_threshold_hours:
        'transfer a phone cancellation within this many hours of the shift',
    inbound_voice_transfer_resistance_enabled:
        'try to resolve a call before transferring it',
    inbound_voice_transfer_unknown_callers_enabled:
        'transfer callers who aren’t caregivers',
    include_sms_body_in_email_notification:
        'include the text message in email notifications',
    mark_decline_outreach_messages_as_read: 'mark declines as read',
    mark_positive_shift_confirmation_messages_as_read:
        'mark confirmations as read',
    notify_only_assigned_coordinators: 'notify only assigned coordinators',
    plan_of_care_duties_sms_enabled: 'text caregivers about missing care tasks',
    require_wellsky_caregiver_rate_to_fill:
        'require a WellSky caregiver rate to fill',
    require_wellsky_client_rate_to_fill:
        'require a WellSky client rate to fill',
    unresponsive_caregiver_threshold: 'unresponsive caregiver threshold (%)',
    // Callout settings, reached through `update_callout_settings`. The
    // sidebar has no settings write.
    allow_auto_assign: 'allow auto-assign',
    allow_custom: 'allow a custom outreach response option',
    auto_assign_first_responder_default:
        'auto-assign the first responder (default)',
    auto_start_outreach_default: 'auto-start outreaches (default)',
    response_requery_window_hours:
        'how long a caregiver’s last response stays fresh (hours)',
    skip_busy_caregivers: 'skip caregivers already working',
    // Shift-confirmation settings. `enabled` is this section's master switch
    // and no other section writes a bare `enabled` key.
    enable_relative_dates: 'use relative dates',
    enabled: 'shift confirmations',
    caregiver_quiet_hours_windows: 'caregiver quiet hours schedule',
    client_quiet_hours_windows: 'client quiet hours schedule',
    // Care-plan settings
    allow_client_identifying_info: 'allow client identifying info in texts',
    // Voice settings
    unknown_caller_auto_transfer_enabled: 'auto-transfer unknown callers',
};

function humanizeSnakeCase(value: string): string {
    return value.replace(/_/g, ' ');
}

function settingLabel(key: string): string {
    // Own-property check: `key` comes from model-supplied tool arguments, so
    // a bare index could resolve a prototype member.
    const label = Object.prototype.hasOwnProperty.call(
        SETTING_FIELD_LABELS,
        key,
    )
        ? SETTING_FIELD_LABELS[key]
        : undefined;
    return label ?? humanizeSnakeCase(key);
}

// Enum values are backend vocabulary too: "caregivers over the weekly cap →
// hide" says nothing about what the scheduler will actually see. Phrasing
// follows the tool's own description in tools/recommendation_defaults.py.
const SETTING_VALUE_LABELS: Record<string, Record<string, string>> = {
    // Terse on purpose: these render as "<label> <from> → <to>", and the
    // label already supplies "caregivers over the weekly cap".
    overtime_behavior: {
        flag: 'flagged',
        hide: 'hidden',
    },
};

function recommendationDefaultValueLabel(value: unknown, key: string): string {
    if (typeof value === 'string') {
        const valueLabel = SETTING_VALUE_LABELS[key]?.[value];
        if (valueLabel !== undefined) {
            return valueLabel;
        }
    }
    if (value === null || value === undefined) {
        return 'no limit';
    }
    if (typeof value === 'boolean') {
        return value ? 'on' : 'off';
    }
    if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : 'none';
    }
    return String(value);
}

/**
 * Current-aware summary for `update_recommendation_defaults`: renders
 * "field current → new" instead of just the new value. `current` is the
 * org's saved defaults fetched by the approval card; only argument keys
 * that exist on that snapshot are echoed, so unexpected argument keys
 * (which could carry IDs) never render. Falls back to the generic
 * changed-settings summary while the snapshot is loading.
 */
export function recommendationDefaultsApprovalSummary(
    args: Record<string, unknown>,
    current: Record<string, unknown> | null,
    context?: ApprovalCopyContext,
): string {
    if (current === null) {
        return changedSettingsSummary(
            args,
            subjectForOrganization('these recommendation defaults', context),
        );
    }
    const subject = subjectForOrganization(
        'these recommendation defaults',
        context,
    );
    const entries = Object.entries(args)
        .filter(([key]) => key in current)
        .map(([key, value]) => ({
            from: recommendationDefaultValueLabel(current[key], key),
            to: recommendationDefaultValueLabel(value, key),
            label: settingLabel(key),
        }));
    // No-op lines like "max daily hours 8 → 8" are dropped — the backend
    // reports them as no_changes too. When every requested value already
    // matches, say so instead of implying a change.
    const changes = entries
        .filter(({ from, to }) => from !== to)
        .map(({ label, from, to }) => `${label} ${from} → ${to}`);
    if (changes.length === 0) {
        if (entries.length > 0) {
            const unchanged = entries
                .map(({ label, from }) => `${label} is already ${from}`)
                .join('; ');
            return `No values will change: ${unchanged}.`;
        }
        return `Phoebe will update ${subject}.`;
    }
    return `Phoebe will change ${subject}: ${changes.join('; ')}.`;
}

function changedSettingsSummary(
    args: Record<string, unknown>,
    subject: string,
): string {
    // Only echo boolean/number values — settings args are toggles and
    // small numbers. ID keys and free-form strings (which can carry
    // UUIDs) are never rendered.
    const changes = Object.entries(args)
        .filter(
            ([key, value]) =>
                !/_ids?$/.test(key) &&
                (typeof value === 'boolean' || typeof value === 'number'),
        )
        .map(([key, value]) =>
            typeof value === 'boolean'
                ? `${settingLabel(key)} → ${value ? 'on' : 'off'}`
                : `${settingLabel(key)} → ${value}`,
        );
    if (changes.length === 0) {
        return `Phoebe will update ${subject}.`;
    }
    return `Phoebe will change ${subject}: ${changes.join('; ')}.`;
}

function organizationSettingValueLabel(value: unknown): string {
    if (value === null || value === undefined) {
        return 'cleared';
    }
    if (typeof value === 'boolean') {
        return value ? 'on' : 'off';
    }
    if (typeof value === 'number') {
        return String(value);
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return 'cleared';
        }
        const preview =
            trimmed.length > 80 ? `${trimmed.slice(0, 77)}...` : trimmed;
        return `“${preview}”`;
    }
    if (Array.isArray(value)) {
        return `${value.length} item${value.length === 1 ? '' : 's'}`;
    }
    if (isRecord(value)) {
        return 'structured value';
    }
    return String(value);
}

function changedOrganizationSettingsSummary(
    updates: Record<string, unknown>,
    subject: string,
): string {
    const changes = Object.entries(updates)
        .filter(([key]) => !/_ids?$/.test(key))
        .map(
            ([key, value]) =>
                `${settingLabel(key)} → ${organizationSettingValueLabel(
                    value,
                )}`,
        );
    if (changes.length === 0) {
        return `Phoebe will update ${subject}.`;
    }
    return `Phoebe will change ${subject}: ${changes.join('; ')}.`;
}

const PHONE_FORWARDING_FIELD_LABELS: Readonly<Record<string, string>> = {
    allowed_call_routing_destination_ids: 'allowed transfer options',
    default_call_routing_destination_id: 'default transfer option',
    phoebe_phone_number_id: 'Phoebe phone number',
    safe_transfer_caller_id: 'safe transfer caller ID',
};

const PHONE_FORWARDING_VISIBLE_ID_KEYS: ReadonlySet<string> = new Set(
    Object.keys(PHONE_FORWARDING_FIELD_LABELS),
);

function phoneForwardingValueLabel(key: string, value: unknown): string {
    switch (key) {
        case 'allowed_call_routing_destination_ids':
            if (Array.isArray(value)) {
                return `${value.length} item${value.length === 1 ? '' : 's'}`;
            }
            return organizationSettingValueLabel(value);
        case 'default_call_routing_destination_id':
        case 'phoebe_phone_number_id':
            return value === null || value === undefined
                ? 'cleared'
                : 'selected';
        default:
            return organizationSettingValueLabel(value);
    }
}

function changedPhoneForwardingSettingsSummary(
    updates: Record<string, unknown>,
    subject: string,
): string {
    const changes = Object.entries(updates)
        .filter(
            ([key]) =>
                !/_ids?$/.test(key) ||
                PHONE_FORWARDING_VISIBLE_ID_KEYS.has(key),
        )
        .map(([key, value]) => {
            const label =
                PHONE_FORWARDING_FIELD_LABELS[key] ?? settingLabel(key);
            return `${label} → ${phoneForwardingValueLabel(key, value)}`;
        });
    if (changes.length === 0) {
        return `Phoebe will update ${subject}.`;
    }
    return `Phoebe will change ${subject}: ${changes.join('; ')}.`;
}

function organizationSettingsSectionLabel(value: unknown): string {
    switch (value) {
        case 'general':
            return 'general organization settings';
        case 'callout':
            return 'callout settings';
        case 'voice':
            return 'voice settings';
        case 'shift_confirmation':
            return 'shift-confirmation settings';
        case 'care_plan':
            return 'care-plan settings';
        case 'activity_feed':
            return 'activity-feed settings';
        case 'qa_voice_agent':
            return 'QA voice-agent settings';
        default:
            return 'organization settings';
    }
}

/**
 * Combine two independently-applied content args into one preview.
 * Only coalesce when one is absent — when a tool sends/applies both
 * values, hiding the second would under-disclose what the coordinator
 * is approving.
 */
function labeledPairPreview(
    first: string | null,
    firstLabel: string,
    second: string | null,
    secondLabel: string,
): string | null {
    if (first && second) {
        return `${firstLabel}:\n${first}\n\n${secondLabel}:\n${second}`;
    }
    return first ?? second;
}

function changedFieldLabels(
    args: Record<string, unknown>,
    fieldLabels: Readonly<Record<string, string>>,
): Array<string> {
    return Object.entries(fieldLabels)
        .filter(([key]) => args[key] !== undefined && args[key] !== null)
        .map(([, label]) => label);
}

function changedFieldsSummary(
    args: Record<string, unknown>,
    fieldLabels: Readonly<Record<string, string>>,
    subject: string,
): string {
    const labels = changedFieldLabels(args, fieldLabels);
    if (labels.length === 0) {
        return `Phoebe will update this ${subject}’s record.`;
    }
    return `Phoebe will update this ${subject}’s ${labels.join(', ')}.`;
}

// Mirrors the backend SyncEntityType values; an unrecognized value
// falls back to generic copy rather than rendering model input.
const SYNC_ENTITY_TYPE_LABELS: Readonly<Record<string, string>> = {
    authorizations: 'authorization data',
    care_coordinators: 'care coordinator data',
    caregivers: 'caregiver data',
    clients: 'client data',
    shifts: 'shift data',
};

const SHIFT_UPDATE_FIELD_LABELS: Readonly<Record<string, string>> = {
    internal_shift_notes: 'internal notes',
    notes_for_caregiver: 'caregiver notes',
    patient_address: 'address',
    patient_city: 'city',
    patient_name: 'client name',
    patient_state: 'state',
    shift_end_time: 'end time',
    shift_start_time: 'start time',
};

type SendSmsRecipient = {
    readonly name: string;
    readonly displayName: string | null;
    readonly content: string | null;
    readonly hasOwnContent: boolean;
};

/**
 * Read `send_sms`'s recipient list for the card. `name` is a display-only
 * argument, so an absent or ID-shaped one degrades to a generic noun rather
 * than putting a UUID in front of the coordinator.
 */
function sendSmsRecipients(
    args: Record<string, unknown>,
    sharedContent: string | null,
): Array<SendSmsRecipient> {
    const raw = args['recipients'];
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw.map((entry) => {
        const record = isRecord(entry) ? entry : {};
        const ownContent = getStringValue(record, 'content');
        const displayName = safeDisplayName(getStringValue(record, 'name'));
        return {
            name:
                displayName ??
                (record['conversation_id'] === undefined
                    ? 'caregiver'
                    : 'conversation'),
            displayName,
            content: ownContent ?? sharedContent,
            hasOwnContent: ownContent !== null,
        };
    });
}

type ApprovalCopyBuilder = (
    args: Record<string, unknown>,
    context?: ApprovalCopyContext,
) => ApprovalCopy;

function formatScheduledStart(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }
    // `start_at` is an org-local wall-clock time. Parse its components and
    // render via UTC so the displayed time is not shifted into the viewer's
    // browser timezone (the org timezone is not available on this card).
    // Only a naive value is rendered; an offset-aware/`Z` value can't be
    // shown in org-local terms here, so it falls back to the generic copy
    // rather than display a misleading time.
    const match = value.match(
        /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2})?$/,
    );
    if (!match) {
        return null;
    }
    const [, year, month, day, hour, minute] = match;
    const parsed = new Date(
        Date.UTC(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hour),
            Number(minute),
        ),
    );
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }
    return parsed.toLocaleString(undefined, {
        timeZone: 'UTC',
        dateStyle: 'medium',
        timeStyle: 'short',
    });
}

const AUTOMATION_WEEKDAY_LABELS: ReadonlyArray<string> = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];

function formatAutomationSchedule(value: unknown): string | null {
    if (!isRecord(value)) {
        return null;
    }
    const kind = getStringValue(value, 'kind');
    const time = getStringValue(value, 'time');
    if (!kind || !time) {
        return null;
    }
    if (kind === 'daily') {
        return `Daily at ${time} (agency local time)`;
    }
    const weekday = value['weekday'];
    if (
        kind !== 'weekly' ||
        typeof weekday !== 'number' ||
        !Number.isInteger(weekday) ||
        weekday < 0 ||
        weekday >= AUTOMATION_WEEKDAY_LABELS.length
    ) {
        return null;
    }
    return (
        `Weekly on ${AUTOMATION_WEEKDAY_LABELS[weekday]} at ${time} ` +
        '(agency local time)'
    );
}

function formatAutomationEvent(value: unknown): string | null {
    if (!isRecord(value)) {
        return null;
    }
    const label = safeDisplayName(getStringValue(value, 'label'));
    if (!label) {
        return null;
    }
    const eventFilter = value['event_filter'];
    if (!isRecord(eventFilter)) {
        return `Event: ${label}`;
    }
    const filters = Object.entries(eventFilter)
        .filter(
            ([key, filterValue]) =>
                Boolean(key) && typeof filterValue === 'string',
        )
        .map(([key, filterValue]) => `${key} = ${filterValue}`);
    return filters.length > 0
        ? `Event: ${label} (fixed filter: ${filters.join(', ')})`
        : `Event: ${label}`;
}

function automationTrigger(args: Record<string, unknown>): {
    readonly schedule: unknown;
    readonly event: unknown;
} {
    const resolvedTrigger = isRecord(args['resolved_trigger'])
        ? args['resolved_trigger']
        : null;
    const trigger = isRecord(args['trigger']) ? args['trigger'] : null;
    return {
        schedule:
            resolvedTrigger?.['type'] === 'schedule'
                ? resolvedTrigger['schedule']
                : (trigger?.['schedule'] ?? args['schedule']),
        event: resolvedTrigger?.['type'] === 'event' ? resolvedTrigger : null,
    };
}

function automationSubjectNoun(args: Record<string, unknown>): string {
    const trigger = automationTrigger(args);
    return trigger.event !== null
        ? 'event-triggered review'
        : 'scheduled review';
}

function automationChangeDetails(
    args: Record<string, unknown>,
    action: string | null,
): ApprovalCopy['detailList'] | undefined {
    if (action !== 'create' && action !== 'edit' && action !== 'enable') {
        return undefined;
    }

    const items: Array<string> = [];
    const name =
        action === 'enable'
            ? null
            : safeDisplayName(getStringValue(args, 'name'));
    const prompt =
        action === 'enable' ? null : getStringValue(args, 'prompt')?.trim();
    const trigger = automationTrigger(args);
    const schedule = formatAutomationSchedule(trigger.schedule);
    const event = formatAutomationEvent(trigger.event);
    const enabled =
        typeof args['enabled'] === 'boolean'
            ? args['enabled']
                ? 'Enabled'
                : 'Disabled'
            : null;

    if (name) {
        items.push(`Name: ${name}`);
    }
    if (prompt) {
        items.push(`Prompt: ${prompt}`);
    }
    if (schedule) {
        items.push(`Schedule: ${schedule}`);
    }
    if (event) {
        items.push(event);
    }
    if (action === 'create') {
        items.push(`Enabled: ${enabled ?? 'Enabled'}`);
    } else if (action === 'enable') {
        items.push('Enabled: Enabled');
    } else if (enabled) {
        items.push(`Enabled: ${enabled}`);
    }

    return items.length > 0
        ? {
              label: 'Requested changes',
              itemNoun: 'field',
              items,
          }
        : undefined;
}

const APPROVAL_COPY_BUILDERS: Record<string, ApprovalCopyBuilder> = {
    assign_caregiver: (args) => {
        const shiftPhrase = counted(
            countOf(args['shift_ids']),
            'shift',
            'the selected shifts',
        );
        const notConfirmedSentence =
            args['assign_without_response'] === true
                ? ' This caregiver hasn’t confirmed interest in Phoebe.'
                : '';
        return {
            title: 'Assign this caregiver?',
            summary:
                `Phoebe will assign ${shiftPhrase} to the chosen caregiver ` +
                `and mark them filled.${notConfirmedSentence} The assigned ` +
                'caregiver and caregivers who replied interested will get a ' +
                'text. Your Assign & Fill setting decides whether caregivers ' +
                'who never responded are also texted. Phoebe will write the ' +
                'assignment back to the connected EHR for each shift linked ' +
                'there.',
            messagePreview: null,
        };
    },
    manage_automation: (args) => {
        const action = getStringValue(args, 'action');
        const name = safeDisplayName(getStringValue(args, 'name'));
        const resolvedAutomationName = safeDisplayName(
            getStringValue(args, 'resolved_automation_name'),
        );
        const description = safeDisplayName(
            getStringValue(args, 'description'),
        );
        const subjectNoun = automationSubjectNoun(args);
        const subjectName =
            action === 'edit' || action === 'enable' || action === 'delete'
                ? resolvedAutomationName
                : name;
        const subject = subjectName
            ? `the ${subjectNoun} “${subjectName}”`
            : `this ${subjectNoun}`;
        const actionLabel =
            action === 'edit'
                ? 'Update'
                : action === 'enable'
                  ? 'Enable'
                  : action === 'delete'
                    ? 'Delete'
                    : 'Create';
        return {
            title: `${actionLabel} ${subject}?`,
            summary:
                `Phoebe will ${actionLabel.toLowerCase()} ${subject}.` +
                (description ? ` Purpose: ${description}.` : ''),
            messagePreview: null,
            detailList: automationChangeDetails(args, action),
        };
    },
    draft_agent_outreach: (args) => {
        const scheduledStart = formatScheduledStart(args['start_at']);
        const shiftDates = stringList(args['shift_dates']);
        // start_at implies auto_start even when the time can't be rendered.
        const autoStarts =
            args['auto_start'] === true || typeof args['start_at'] === 'string';
        const urgent = args['outreach_urgency'] === 'urgent';
        const contactSentence =
            scheduledStart !== null
                ? `At ${scheduledStart}, the outreach starts automatically and Phoebe contacts all recommended caregivers. There is no further review before messages go out.`
                : autoStarts
                  ? 'Once caregiver recommendations are ready, the outreach starts automatically and Phoebe contacts all recommended caregivers. There is no further review before messages go out.'
                  : 'No caregivers are contacted until the outreach is started.';
        const preparationSentence = urgent
            ? 'and immediately stage a broad list of recommended caregivers without individual review'
            : 'and prepare caregiver recommendations';
        return {
            title: autoStarts
                ? urgent
                    ? 'Create and auto-start this urgent outreach?'
                    : 'Create and auto-start this outreach?'
                : urgent
                  ? 'Create this urgent outreach?'
                  : 'Create this outreach?',
            summary: `Phoebe will create an ${urgent ? 'urgent ' : ''}outreach for ${counted(
                countOf(args['shift_ids']),
                'shift',
                'the selected shifts',
            )} ${preparationSentence}. ${contactSentence}`,
            messagePreview: null,
            ...(shiftDates === null
                ? {}
                : {
                      detailList: {
                          label: 'Selected shifts',
                          itemNoun: 'shift',
                          items: shiftDates,
                      },
                  }),
        };
    },
    manage_outreach_start_with_contacts: (args) => ({
        title: 'Start this outreach?',
        summary: `Phoebe will start calling and texting ${counted(
            countOf(args['caregiver_shift_ids']),
            'caregiver',
            'the selected caregivers',
        )} for this outreach.`,
        messagePreview: null,
    }),
    manage_outreach_add_contact_attempts: (args) => ({
        title: 'Send more outreach messages?',
        summary: `Phoebe will reach out to ${counted(
            countOf(args['caregiver_ids']),
            'more caregiver',
            'the selected caregivers',
        )} on this outreach.`,
        messagePreview: null,
    }),
    fill_callout_shifts: (args, context) => {
        // State who gets the "shift filled" text before the coordinator
        // approves, not after it sends (PHO-14446). An explicit argument
        // wins; otherwise the org's Assign & Fill default applies.
        const notifyNonResponders =
            typeof args['notify_non_responders'] === 'boolean'
                ? args['notify_non_responders']
                : (context?.notifyNonRespondersDefault ?? null);
        let nonResponderSentence: string;
        if (notifyNonResponders === null) {
            nonResponderSentence =
                'Your Assign & Fill setting decides whether caregivers who ' +
                'never responded are texted.';
        } else if (notifyNonResponders) {
            nonResponderSentence =
                'Caregivers who never responded will also be texted.';
        } else {
            nonResponderSentence =
                'Caregivers who never responded won’t be texted.';
        }
        // Naming the caregiver and flagging the missing acceptance IS the
        // safeguard on assign_without_response — approving "the chosen
        // caregiver" is how uninterested caregivers got assigned before
        // (PHO-14544). Do not reduce this to generic wording.
        const caregiverName = safeDisplayName(context?.caregiverName ?? null);
        const shiftPhrase = counted(
            countOf(args['shift_ids']),
            'shift',
            'the selected shifts',
        );
        const withoutResponse = args['assign_without_response'] === true;
        const notConfirmedSentence = withoutResponse
            ? `${caregiverName ?? 'This caregiver'} hasn’t confirmed interest in Phoebe. `
            : '';
        return {
            title: caregiverName
                ? `Assign ${caregiverName} to ${shiftPhrase}?`
                : 'Fill these shifts?',
            summary:
                (caregiverName
                    ? `Phoebe will assign ${shiftPhrase} to ${caregiverName} and mark them filled. `
                    : `Phoebe will assign ${shiftPhrase} to the chosen caregiver and mark them filled. `) +
                notConfirmedSentence +
                'The assigned caregiver and caregivers who replied ' +
                `interested will get a text. ${nonResponderSentence}`,
            messagePreview: null,
        };
    },
    assign_shifts_to_caregiver: (args) => ({
        title: 'Assign these shifts?',
        summary: `Phoebe will assign ${counted(
            countOf(args['shift_ids']),
            'shift',
            'the selected shifts',
        )} to the chosen caregiver on the schedule, and write the assignment back to the connected EHR (such as WellSky) for any shift linked there.`,
        messagePreview: null,
    }),
    notify_caregivers: (args) => ({
        title: 'Notify caregivers?',
        summary:
            'Phoebe will text the caregivers on this outreach to let ' +
            'them know the shifts were handled.',
        // The workflow sends both bodies: one to the assigned
        // caregiver, the other to everyone else.
        messagePreview: labeledPairPreview(
            getStringValue(args, 'custom_message_assigned'),
            'To assigned caregiver',
            getStringValue(args, 'custom_message_not_assigned'),
            'To other interested caregivers',
        ),
    }),
    send_sms_to_caregiver: (args) => ({
        title: 'Send this text?',
        summary: 'Phoebe will text this caregiver:',
        messagePreview: getStringValue(args, 'body'),
    }),
    create_shift: (args) => {
        const patientName = getStringValue(args, 'patient_name');
        return {
            title: 'Create this shift?',
            summary: `Phoebe will add a new shift${
                patientName ? ` for ${patientName}` : ''
            } to the schedule.`,
            messagePreview: null,
        };
    },
    clock_shift_in: (args) => {
        const clientName = getStringValue(args, 'client_name');
        const startLabel = getStringValue(args, 'shift_start_time');
        return {
            title: clientName
                ? `Clock ${clientName}’s shift in?`
                : 'Clock this shift in?',
            summary: `Phoebe will clock the assigned caregiver into this shift in the connected EHR now${startLabel ? ` (scheduled ${startLabel})` : ''}. The EHR’s current server time will be used.`,
            messagePreview: null,
        };
    },
    clock_shift_out: (args) => {
        const clientName = getStringValue(args, 'client_name');
        const startLabel = getStringValue(args, 'shift_start_time');
        return {
            title: clientName
                ? `Clock ${clientName}’s shift out?`
                : 'Clock this shift out?',
            summary: `Phoebe will close the caregiver’s open work session for this shift in the connected EHR now${startLabel ? ` (scheduled ${startLabel})` : ''}. The EHR’s current server time will be used.`,
            messagePreview: null,
        };
    },
    delete_agent_created_shift: (args) => {
        const clientName = getStringValue(args, 'client_name');
        const startLabel = getStringValue(args, 'shift_start_time');
        return {
            title: clientName
                ? `Permanently delete ${clientName}’s shift?`
                : 'Permanently delete this shift?',
            summary: `Phoebe will permanently delete this agent-created one-off shift from the connected EHR and Phoebe${startLabel ? ` (scheduled ${startLabel})` : ''}. This cannot be undone.`,
            messagePreview: null,
        };
    },
    update_outreach_metadata: (args) => ({
        title: 'Update this outreach?',
        summary:
            'Phoebe will update this outreach’s instructions and ' +
            'notification settings.',
        messagePreview: labeledPairPreview(
            getStringValue(args, 'instructions'),
            'Instructions',
            getStringValue(args, 'opening_line'),
            'Opening line',
        ),
    }),
    set_tag_matching_importance: (args) => {
        const tag = getStringValue(args, 'tag');
        const importance = getStringValue(args, 'importance');
        const scope = getStringValue(args, 'client_id')
            ? 'for this client'
            : 'across the organization';
        // Each phrase restates what the importance actually does.
        // hard/preferred/ignore/default follow the tool description in
        // tools/tag_overrides.py; `soft` is undocumented there and is
        // inferred from _IMPORTANCE_RANK (soft ranks below preferred).
        // Interpolating the raw
        // value produced "treat the “CNA required” tag as hard", which tells
        // a scheduler nothing about who gets suggested.
        const effects: Record<string, string> =
            tag === null
                ? {}
                : {
                      hard: `only suggest caregivers who match the “${tag}” tag`,
                      preferred: `rank caregivers who don’t match the “${tag}” tag lower, but still suggest them`,
                      soft: `let the “${tag}” tag nudge ranking slightly`,
                      ignore: `ignore the “${tag}” tag when matching`,
                      default: `reset the “${tag}” tag to its usual importance`,
                  };
        // `importance` is model-supplied, so a bare index could resolve a
        // prototype member (`constructor`) into the sentence.
        const effect =
            importance !== null &&
            Object.prototype.hasOwnProperty.call(effects, importance)
                ? (effects[importance] ?? null)
                : null;
        return {
            title: 'Change how this tag is matched?',
            summary: effect
                ? `Phoebe will ${effect} ${scope}.`
                : 'Phoebe will change how much a tag affects caregiver ' +
                  'matching.',
            messagePreview: null,
        };
    },
    save_caregiver_memory_instruction: (args, context) => ({
        title: 'Save this caregiver note?',
        summary: `Phoebe will remember this about ${caregiverMemoryTarget(
            args,
            context,
        )}:`,
        messagePreview: getStringValue(args, 'instruction'),
    }),
    save_client_memory_instruction: (args, context) => ({
        title: 'Save this client note?',
        summary: `Phoebe will remember this about ${clientMemoryTarget(
            args,
            context,
        )}:`,
        messagePreview: getStringValue(args, 'instruction'),
    }),
    update_outreach_filter_rule: () => ({
        title: 'Update outreach rules?',
        summary: 'Phoebe will change who is included in automated outreach.',
        messagePreview: null,
    }),
    update_organization_general_settings: (args, context) => ({
        title: approvalQuestionForOrganization(
            'Update organization settings',
            context,
        ),
        summary: changedSettingsSummary(
            args,
            subjectForOrganization('these organization settings', context),
        ),
        messagePreview: null,
    }),
    update_callout_settings: (args, context) => ({
        title: approvalQuestionForOrganization(
            'Update callout settings',
            context,
        ),
        summary: changedSettingsSummary(
            args,
            subjectForOrganization('these callout settings', context),
        ),
        messagePreview: null,
    }),
    update_recommendation_defaults: (args, context) => ({
        title: approvalQuestionForOrganization(
            'Update recommendation defaults',
            context,
        ),
        summary: changedSettingsSummary(
            args,
            subjectForOrganization('these recommendation defaults', context),
        ),
        messagePreview: null,
    }),
    update_callout_excluded_caregivers: (args) => {
        const count = counted(
            countOf(args['caregiver_ids']),
            'caregiver',
            'the selected caregivers',
        );
        const action = getStringValue(args, 'action');
        return {
            title: 'Update excluded caregivers?',
            summary:
                action === 'add'
                    ? `Phoebe will exclude ${count} from callouts.`
                    : action === 'remove'
                      ? `Phoebe will allow ${count} to receive callouts ` +
                        'again.'
                      : 'Phoebe will change which caregivers are excluded ' +
                        `from callouts (${count}).`,
            messagePreview: null,
        };
    },
    update_client_record: (args) => ({
        title: 'Update this client?',
        summary: changedFieldsSummary(
            args,
            {
                care_plan_summary: 'care plan summary',
                default_notify_on_fill: 'notify-on-fill default',
            },
            'client',
        ),
        messagePreview: null,
    }),
    update_client_phone_preference: (args) => {
        const preference = getStringValue(args, 'client_call_phone_preference');
        return {
            title: 'Update this client’s phone preference?',
            summary: preference
                ? 'Phoebe will set this client’s call phone preference ' +
                  `to “${humanizeSnakeCase(preference)}”.`
                : 'Phoebe will change which phone number is used to ' +
                  'call this client.',
            messagePreview: null,
        };
    },
    update_caregiver_record: (args) => ({
        title: 'Update this caregiver?',
        summary: changedFieldsSummary(
            args,
            {
                agent_instructions: 'agent instructions',
                desired_weekly_hours: 'desired weekly hours',
                employment_type: 'employment type',
                primary_language: 'primary language',
                roles: 'roles',
                supported_languages: 'languages',
            },
            'caregiver',
        ),
        messagePreview: null,
    }),
    assign_client_care_coordinator: () => ({
        title: 'Assign a care coordinator?',
        summary: 'Phoebe will assign the selected coordinator to this client.',
        messagePreview: null,
    }),
    remove_client_care_coordinator: () => ({
        title: 'Remove a care coordinator?',
        summary:
            'Phoebe will remove the selected coordinator from this client.',
        messagePreview: null,
    }),
    add_caregiver_weekly_availability: (args) => ({
        title: 'Add weekly availability?',
        summary: `Phoebe will add a recurring weekly ${
            getStringValue(args, 'status') ?? 'availability'
        } window to this caregiver’s schedule.`,
        messagePreview: null,
    }),
    add_caregiver_one_off_availability: (args) => ({
        title: 'Add one-time availability?',
        summary: `Phoebe will add a one-time ${
            getStringValue(args, 'status') ?? 'availability'
        } window to this caregiver’s schedule.`,
        messagePreview: null,
    }),
    delete_caregiver_availability: () => ({
        title: 'Delete availability?',
        summary:
            'Phoebe will remove an availability window from this ' +
            'caregiver’s schedule.',
        messagePreview: null,
    }),
    update_shift: (args) => {
        if (args['unassign_caregiver'] === true) {
            const labels = changedFieldLabels(args, SHIFT_UPDATE_FIELD_LABELS);
            return {
                title: 'Remove the caregiver from this shift?',
                summary:
                    'Phoebe will remove the assigned caregiver and mark ' +
                    'the shift as needing coverage' +
                    (labels.length > 0
                        ? `, and update the shift’s ${labels.join(', ')}.`
                        : '.') +
                    ' Phoebe will also write the removal back to the source ' +
                    'EHR.',
                messagePreview: null,
            };
        }
        return {
            title: 'Update this shift?',
            summary: changedFieldsSummary(
                args,
                SHIFT_UPDATE_FIELD_LABELS,
                'shift',
            ),
            messagePreview: null,
        };
    },
    trigger_ehr_sync: (args) => {
        const entityType = getStringValue(args, 'entity_type');
        const label = entityType
            ? SYNC_ENTITY_TYPE_LABELS[entityType]
            : undefined;
        return {
            title: 'Sync from the EHR?',
            summary: label
                ? `Phoebe will pull the latest ${label} from the EHR.`
                : 'Phoebe will pull the latest data from the EHR.',
            messagePreview: null,
        };
    },
    cancel_callout_shifts: (args) => ({
        title: 'Cancel these shifts?',
        summary: `Phoebe will cancel ${counted(
            countOf(args['shift_ids']),
            'shift',
            'the selected shifts',
        )} on this outreach${
            args['send_notifications'] === true
                ? ' and notify the caregivers'
                : ''
        }.`,
        messagePreview: null,
    }),
    cancel_shift: (args) => {
        const cancelledBy = getStringValue(args, 'cancelled_by');
        const reason = getStringValue(args, 'reason');
        const clientName = getStringValue(args, 'client_name');
        // The card is the last line of defense before a destructive
        // external write: identify exactly which shift is being cancelled.
        // The tool verifies these fields against the shift and refuses on
        // any mismatch, so the card's claims cannot drift from reality.
        // shift_start_time is already the org-local label the shift tools
        // emit — render it verbatim; converting through Date would shift
        // it into the viewer's browser timezone.
        const startLabel = getStringValue(args, 'shift_start_time');
        return {
            title: clientName
                ? `Cancel ${clientName}’s shift?`
                : 'Cancel this shift?',
            summary:
                (cancelledBy === 'caregiver'
                    ? 'Phoebe will record the shift' +
                      (startLabel ? ` on ${startLabel}` : '') +
                      ' as cancelled by the caregiver'
                    : 'Phoebe will cancel the shift' +
                      (startLabel ? ` on ${startLabel}` : '')) +
                ': in the source EHR first when the shift is linked, then ' +
                'in Phoebe. Pending clock reminders are cancelled too.',
            messagePreview: reason ?? null,
        };
    },
    add_shift_note_to_ehr: (args, context) => {
        const note = getStringValue(args, 'note');
        // The write path selects its provider from this exact shift's source
        // snapshot, so the approval must use the same fact. Organization-level
        // connection or onboarding data can disagree after an EHR migration.
        const ehrName = ehrDisplayName(context?.shiftSourceSystem);
        const ordinaryNoteSummary =
            ehrName === 'WellSky'
                ? 'Phoebe will add a WellSky shift note.'
                : ehrName === 'AxisCare'
                  ? 'Phoebe will add this detail to the AxisCare ' +
                    'Internal Memo.'
                  : 'Phoebe will save this detail to the shift in the source EHR.';
        return {
            title: ehrName
                ? `Save this detail to the ${ehrName} shift?`
                : 'Save this detail to the EHR shift?',
            summary: ordinaryNoteSummary,
            messagePreview: note ?? null,
        };
    },
    override_contact_attempt_response: (args) => {
        const response = getStringValue(args, 'response');
        return {
            title: 'Update caregiver response?',
            summary: response
                ? 'Phoebe will record the caregiver’s response as ' +
                  `“${humanizeSnakeCase(response)}”.`
                : 'Phoebe will correct how the caregiver’s response ' +
                  'was recorded.',
            messagePreview: null,
        };
    },
    add_outreach_subscriber: () => ({
        title: 'Add a subscriber?',
        summary:
            'Phoebe will subscribe the selected teammate to updates ' +
            'about this outreach.',
        messagePreview: null,
    }),
    remove_outreach_subscriber: () => ({
        title: 'Remove a subscriber?',
        summary:
            'Phoebe will unsubscribe the selected teammate from updates ' +
            'about this outreach.',
        messagePreview: null,
    }),
    // "Staged" is backend vocabulary the rest of the product dropped; the
    // approval card already implies the message is waiting on the
    // coordinator, so the word carries nothing.
    approve_staged_message: (args) => ({
        title: 'Send this reply?',
        summary: 'Phoebe will send this reply to the caregiver.',
        messagePreview: getStringValue(args, 'updated_content'),
    }),
    reject_staged_message: () => ({
        title: 'Discard this reply?',
        summary: 'Phoebe will discard this reply so it is not sent.',
        messagePreview: null,
    }),
    edit_staged_message_content: (args) => ({
        title: 'Edit this reply before sending?',
        summary: 'Phoebe will rewrite the reply to:',
        messagePreview: getStringValue(args, 'updated_content'),
    }),
    send_one_off_sms_in_conversation: (args) => ({
        title: 'Send this text?',
        summary: 'Phoebe will send this in the caregiver’s primary language:',
        messagePreview: getStringValue(args, 'content'),
    }),
    place_one_off_voice_call_in_conversation: (args) => ({
        title: 'Place this call?',
        summary: 'Phoebe will place a voice call to this contact about:',
        messagePreview: labeledPairPreview(
            getStringValue(args, 'instructions'),
            'Call purpose',
            getStringValue(args, 'opening_line'),
            'Opening line',
        ),
    }),
    update_playbook_item_tool: (args) => {
        const title = getStringValue(args, 'title');
        return {
            title: 'Update this playbook rule?',
            summary: title
                ? `Phoebe will update the “${title}” playbook rule.`
                : 'Phoebe will update this playbook rule.',
            messagePreview: null,
        };
    },
    apply_playbook_natural_language_edit: (args) => ({
        title: 'Update this playbook?',
        summary: 'Phoebe will apply this change to the playbook:',
        messagePreview: getStringValue(args, 'request_text'),
    }),
    apply_reliability_policy_request_tool: (args) => ({
        title: 'Update the reliability policy?',
        summary: 'Phoebe will apply this change to the reliability policy:',
        messagePreview: getStringValue(args, 'request_text'),
    }),
    update_admin_organization_settings: (args, context) => {
        const updates = isRecord(args['updates']) ? args['updates'] : {};
        const subject = organizationSettingsSectionLabel(
            args['settings_family'],
        );
        return {
            title: approvalQuestionForOrganization(
                'Update organization settings',
                context,
            ),
            summary: changedOrganizationSettingsSummary(
                updates,
                subjectForOrganization(subject, context),
            ),
            messagePreview: null,
        };
    },
    write: (args, context) => {
        const fields = isRecord(args['fields']) ? args['fields'] : {};
        const entity = getStringValue(args, 'entity');
        const subject = entity ? entity.replace(/_/g, ' ') : 'settings';
        // Every writable field is a scalar the coordinator must see,
        // strings and enums included; id keys never render.
        const changes = Object.entries(fields)
            .filter(([key]) => !/_ids?$/.test(key))
            .map(
                ([key, value]) =>
                    `${settingLabel(key)} → ${organizationSettingValueLabel(value)}`,
            );
        const target = subjectForOrganization(`these ${subject}`, context);
        return {
            title: approvalQuestionForOrganization(
                `Update ${subject}`,
                context,
            ),
            summary:
                changes.length === 0
                    ? `Phoebe will update ${target}.`
                    : `Phoebe will change ${target}: ${changes.join('; ')}.`,
            messagePreview: null,
        };
    },
    send_sms: (args) => {
        const sharedContent = getStringValue(args, 'content');
        const recipients = sendSmsRecipients(args, sharedContent);
        const count = recipients.length;
        // Per-recipient wording is what the coordinator is approving, so a
        // mixed batch previews every body rather than only the shared one.
        const mixed = recipients.some((recipient) => recipient.hasOwnContent);
        // send_anyway overrides a do-not-contact rule or quiet hours. The
        // coordinator has to see that they are approving the override itself,
        // not an ordinary send.
        const overridesHold = args['send_anyway'] === true;
        const target =
            count > 1
                ? counted(count, 'caregiver', 'these caregivers')
                : (recipients[0]?.displayName ?? 'this caregiver');
        return {
            title: count > 1 ? `Send ${count} texts?` : 'Send this text?',
            summary: overridesHold
                ? `Phoebe will text ${target} even though messages to them are on hold for a do-not-contact rule or quiet hours:`
                : `Phoebe will text ${target}:`,
            messagePreview: mixed
                ? recipients
                      .map(
                          (recipient) =>
                              `${recipient.name}:\n${recipient.content ?? ''}`,
                      )
                      .join('\n\n')
                : sharedContent,
            ...(mixed || count < 2
                ? {}
                : {
                      detailList: {
                          label: 'Recipients',
                          itemNoun: 'recipient',
                          items: recipients.map((recipient) => recipient.name),
                      },
                  }),
        };
    },
    place_voice_call: (args) => {
        const recipient =
            safeDisplayName(getStringValue(args, 'name')) ?? 'this caregiver';
        // send_anyway overrides a do-not-contact rule or quiet hours. The
        // coordinator has to see that they are approving the override itself,
        // not an ordinary call.
        const overridesHold = args['send_anyway'] === true;
        return {
            title: 'Call this caregiver?',
            summary: overridesHold
                ? `Phoebe will have a voice agent call ${recipient} about this, even though calls to them are on hold for a do-not-contact rule or quiet hours:`
                : `Phoebe will have a voice agent call ${recipient} about:`,
            messagePreview: labeledPairPreview(
                getStringValue(args, 'instructions'),
                'Call purpose',
                getStringValue(args, 'opening_line'),
                'Opening line',
            ),
        };
    },
    // `draft_content` is the draft's own wording and `updated_content`
    // replaces it, so whichever is present is the text being approved.
    approve_pending_message: (args) => {
        const updatedContent = getStringValue(args, 'updated_content');
        return {
            title: 'Send this reply?',
            summary:
                updatedContent === null
                    ? 'Phoebe will send this reply to the caregiver:'
                    : 'Phoebe will send this reply to the caregiver instead:',
            messagePreview:
                updatedContent ?? getStringValue(args, 'draft_content'),
        };
    },
    reject_pending_message: () => ({
        title: 'Discard this reply?',
        summary: 'Phoebe will discard this reply so it is not sent.',
        messagePreview: null,
    }),
    edit_pending_message: (args) => ({
        title: 'Edit this reply before sending?',
        summary: 'Phoebe will rewrite the reply to:',
        messagePreview: getStringValue(args, 'updated_content'),
    }),
    add_admin_phone_forwarding: (args, context) => ({
        title: approvalQuestionForOrganization('Add phone forwarding', context),
        summary: changedPhoneForwardingSettingsSummary(
            args,
            subjectForOrganization('phone forwarding settings', context),
        ),
        messagePreview: null,
    }),
    update_admin_phone_forwarding: (args, context) => {
        const updates = isRecord(args['updates']) ? args['updates'] : {};
        return {
            title: approvalQuestionForOrganization(
                'Update phone forwarding',
                context,
            ),
            summary: changedPhoneForwardingSettingsSummary(
                updates,
                subjectForOrganization('phone forwarding settings', context),
            ),
            messagePreview: null,
        };
    },
    add_admin_call_routing_destination: (args, context) => ({
        title: approvalQuestionForOrganization(
            'Add transfer destination',
            context,
        ),
        summary: changedOrganizationSettingsSummary(
            args,
            subjectForOrganization('transfer destination settings', context),
        ),
        messagePreview: null,
    }),
    update_admin_call_routing_destination: (args, context) => {
        const updates = isRecord(args['updates']) ? args['updates'] : {};
        return {
            title: approvalQuestionForOrganization(
                'Update transfer destination',
                context,
            ),
            summary: changedOrganizationSettingsSummary(
                updates,
                subjectForOrganization(
                    'transfer destination settings',
                    context,
                ),
            ),
            messagePreview: null,
        };
    },
};

const INTERNAL_ADMIN_APPROVAL_TOOL_NAMES: ReadonlySet<string> = new Set([
    'add_admin_call_routing_destination',
    'add_admin_phone_forwarding',
    'update_admin_organization_settings',
    'update_admin_call_routing_destination',
    'update_admin_phone_forwarding',
]);

// These tools have their own approval configuration (the v3 sidebar agent's
// ToolApprovalConfig), so they are curated here but are not part of the legacy
// sidebar manifest.
const SEPARATELY_CONFIGURED_APPROVAL_TOOL_NAMES: ReadonlySet<string> = new Set([
    'approve_pending_message',
    'assign_caregiver',
    'edit_pending_message',
    'place_voice_call',
    'reject_pending_message',
    'send_sms',
    'write',
]);

export const CURATED_APPROVAL_TOOL_NAMES: ReadonlySet<string> = new Set(
    Object.keys(APPROVAL_COPY_BUILDERS).filter(
        (toolName) =>
            !INTERNAL_ADMIN_APPROVAL_TOOL_NAMES.has(toolName) &&
            !SEPARATELY_CONFIGURED_APPROVAL_TOOL_NAMES.has(toolName),
    ),
);

/**
 * Deploy-skew safety net for approval-gated tools without curated copy
 * yet. Shows a humanized action phrase only — never raw args, which can
 * contain UUIDs.
 */
function getFallbackApprovalCopy(toolName: string): ApprovalCopy {
    const actionPhrase =
        toolName === 'Tool execution'
            ? 'running tool'
            : toolName.replace(/_/g, ' ');
    return {
        title: 'Approve this action?',
        summary: `Phoebe wants to: ${actionPhrase}.`,
        messagePreview: null,
    };
}

/**
 * `schedule_self_poke` is gated only when its follow-up declares an action
 * that normally needs approval (PHO-11762), so the approval card fires at
 * scheduling time — while the coordinator is present — instead of at the
 * unattended wake-up. The card then asks about the real follow-up action and
 * states its timing ("Send more outreach messages?" / "In about 10 minutes,
 * Phoebe will do this automatically.") rather than the generic poke, so the
 * coordinator approves what they actually asked for. It lives outside
 * APPROVAL_COPY_BUILDERS because this tool is gated by an argument predicate,
 * not by the manifest set the registry mirrors.
 */
const SCHEDULE_SELF_POKE_TOOL_NAME = 'schedule_self_poke';

/** Approximate, human-friendly phrase for schedule_self_poke's delay. */
function delayMinutesPhrase(value: unknown): string | null {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        return null;
    }
    const minutes = Math.round(value);
    if (minutes < 60) {
        return `${minutes} minute${minutes === 1 ? '' : 's'}`;
    }
    if (minutes < 60 * 24) {
        const hours = Math.round(minutes / 60);
        return `${hours} hour${hours === 1 ? '' : 's'}`;
    }
    const days = Math.round(minutes / (60 * 24));
    return `${days} day${days === 1 ? '' : 's'}`;
}

function scheduleSelfPokeApprovalCopy(
    args: Record<string, unknown>,
    context?: ApprovalCopyContext,
): ApprovalCopy {
    const rawActions = args['pre_approved_actions'];
    const actions = Array.isArray(rawActions)
        ? rawActions.filter(
              (name): name is string =>
                  typeof name === 'string' && name.trim().length > 0,
          )
        : [];
    // A gated follow-up is almost always a single action the user asked for,
    // and every gated tool has curated copy, so borrow that action's title:
    // the card asks the real question instead of "schedule self poke". Only
    // the schedule args are known here, so we keep the summary self-contained
    // rather than borrowing the action's argument-specific summary.
    const onlyAction = actions.length === 1 ? actions[0] : undefined;
    const curatedTitle = onlyAction
        ? APPROVAL_COPY_BUILDERS[onlyAction]?.({}, context).title
        : undefined;
    const title =
        curatedTitle ??
        (actions.length > 1
            ? 'Approve these scheduled actions?'
            : 'Approve this scheduled action?');

    const delayPhrase = delayMinutesPhrase(args['delay_minutes']);
    const whenPrefix = delayPhrase ? `In about ${delayPhrase}, ` : '';
    const subject = actions.length > 1 ? 'these' : 'this';
    return {
        title,
        summary: `${whenPrefix}Phoebe will do ${subject} automatically.`,
        messagePreview: null,
    };
}

export function getApprovalCopy(
    toolName: string,
    args: ApprovalArgs,
    context?: ApprovalCopyContext,
): ApprovalCopy {
    const normalizedArgs = isRecord(args) ? args : {};
    if (toolName === SCHEDULE_SELF_POKE_TOOL_NAME) {
        return scheduleSelfPokeApprovalCopy(normalizedArgs, context);
    }
    const builder = APPROVAL_COPY_BUILDERS[toolName];
    if (!builder) {
        return getFallbackApprovalCopy(toolName);
    }
    return builder(normalizedArgs, context);
}
